import React from 'react';
import '../../../v2-theme.css';

/**
 * V2Button Component
 *
 * Button with v2 design system variants
 * Matches button styles from HTML files exactly
 *
 * @param {string} variant - 'primary' | 'secondary' | 'outline' | 'danger'
 * @param {string} size - 'sm' | 'md' | 'lg'
 * @param {boolean} fullWidth - Whether button should take full width
 * @param {boolean} disabled - Whether button is disabled
 * @param {string} type - Button type (button, submit, reset)
 * @param {function} onClick - Click handler
 * @param {React.ReactNode} children - Button content
 */
const V2Button = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  type = 'button',
  onClick,
  className = '',
  children,
  ...props
}) => {
  const getVariantClass = () => {
    switch (variant) {
      case 'primary':
        // Filled white button (for worker actions)
        return 'v2-btn-primary';
      case 'secondary':
        // Outlined white button (for main submit buttons)
        return 'v2-btn-secondary';
      case 'outline':
        // Dark steel outlined button (for cancel/secondary actions)
        return 'v2-btn-outline';
      case 'danger':
        // Red button for destructive actions
        return 'v2-btn-danger';
      case 'steel':
        // Steel filled button (for restaurant actions)
        return 'v2-btn-steel';
      default:
        return 'v2-btn-primary';
    }
  };

  const getSizeClass = () => {
    switch (size) {
      case 'sm':
        return 'v2-btn-sm';
      case 'lg':
        return 'v2-btn-lg';
      case 'md':
      default:
        return 'v2-btn-md';
    }
  };

  const baseClasses = 'v2-btn';
  const variantClass = getVariantClass();
  const sizeClass = getSizeClass();
  const widthClass = fullWidth ? 'v2-btn-full' : '';
  const disabledClass = disabled ? 'v2-btn-disabled' : '';

  const allClasses = [
    baseClasses,
    variantClass,
    sizeClass,
    widthClass,
    disabledClass,
    className
  ].filter(Boolean).join(' ');

  return (
    <button
      type={type}
      className={allClasses}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

export default V2Button;
